// baseline-data.js - pure helpers for Baseline Testing (Module 2). Catalogue
// lookups, value formatting, best-of-attempts, and the tester queue. No DOM,
// no network - Vitest covers these directly.

import { CONFIG } from './config.js';
import { normalizeCustomTest } from './physical-tests-data.js';

// Custom tests (from the physical_tests table) registered at load so the pure
// lookups below resolve their keys everywhere the app already calls them
// (tester screens, results view, player view) without threading the list
// through every call site. Built-ins always take precedence.
let _custom = [];
export function registerCustomTests(rows) {
  _custom = (rows || []).map(normalizeCustomTest).filter(Boolean);
}

/** Every test the app knows: built-in CONFIG exercises + registered custom. */
export function allTests() {
  const seen = new Set(CONFIG.baseline.exercises.map(e => e.key));
  return [...CONFIG.baseline.exercises, ..._custom.filter(c => !seen.has(c.key))];
}

/** The exercise config row for a key (built-in or custom), or null. */
export function exerciseByKey(key) {
  return CONFIG.baseline.exercises.find(e => e.key === key)
    || _custom.find(e => e.key === key)
    || null;
}

/** Exercise rows for a session's selected keys, in catalogue order. */
export function exercisesForKeys(keys) {
  const set = new Set(keys || []);
  return allTests().filter(e => set.has(e.key));
}

/**
 * Format a result value for display. Stopwatch values are stored as ms and
 * shown as s.ss; rep/gate values are integers shown as-is. Dynamic
 * distribution shows the push+shot split when present.
 */
export function formatValue(exercise, result) {
  if (!exercise || !result) return '-';
  if (exercise.template === 'stopwatch') {
    const ms = Number(result.value);
    if (!isFinite(ms)) return '-';
    return `${(ms / 1000).toFixed(2)}s`;
  }
  if (exercise.template === 'gates_dynamic') {
    const total = Number(result.value);
    const parts = [];
    if (result.value_push != null) parts.push(`P${result.value_push}`);
    if (result.value_shot != null) parts.push(`S${result.value_shot}`);
    return parts.length ? `${total} (${parts.join('+')})` : `${total}`;
  }
  return `${Number(result.value)}`;
}

/**
 * Format an AVERAGE for display. A mean is almost never a whole number, so a
 * rep or gate average would otherwise print as 12.666666666666666. Times are
 * already shown to 2 decimals as seconds, so they go through unchanged.
 */
export function formatAverage(exercise, value) {
  const v = Number(value);
  if (value == null || !isFinite(v)) return '-';
  if (exercise?.template === 'stopwatch') return formatValue(exercise, { value: v });
  return `${Math.round(v * 10) / 10}`;
}

/**
 * Best result among attempts for one exercise - direction-aware (min for
 * stopwatch times, max for reps/gates). Returns the winning row or null.
 */
export function bestResult(exercise, results) {
  const rows = (results || []).filter(r => r.value != null && isFinite(Number(r.value)));
  if (!rows.length) return null;
  const better = exercise?.better === 'min'
    ? (a, b) => Number(a.value) < Number(b.value)
    : (a, b) => Number(a.value) > Number(b.value);
  return rows.reduce((best, r) => (better(r, best) ? r : best), rows[0]);
}

/**
 * Fatigue index for a multi-set test (e.g. the Australian-shuttle pyramid).
 * `splits` are the per-set times in ms. Returns { sets, first, last, fadePct }
 * where fadePct is how much slower the LAST set is than the FIRST (positive =
 * faded/slowed, negative = negative split). Null when there are < 2 sets.
 */
export function fatigueIndex(splits) {
  const xs = (splits || []).map(Number).filter(v => isFinite(v) && v > 0);
  if (xs.length < 2) return null;
  const first = xs[0], last = xs[xs.length - 1];
  return { sets: xs.length, first, last, fadePct: Math.round(((last - first) / first) * 100) };
}

// A result belongs to a registered player OR an external/guest. Either way the
// "subject" is the thing being tested; this id keys the queue + leaderboards so
// guests behave exactly like players (Physical Testing P1).
export function subjectId(r) {
  return r ? (r.player_id || r.external_id) : null;
}

/**
 * Tester queue for one exercise: session subjects (players + guests) who have
 * NO saved result yet for that exercise (the "untested" list). `subjects` are
 * {id, name, external?}; `results` are this session's rows. Untested keep roster
 * order; tested drop off.
 */
export function untestedQueue(exerciseKey, subjects, results) {
  const done = new Set((results || []).filter(r => r.exercise_key === exerciseKey).map(subjectId));
  return (subjects || []).filter(p => !done.has(p.id));
}

/** How many of the session's subjects have at least one result per exercise. */
export function testedCount(exerciseKey, results) {
  return new Set(
    (results || []).filter(r => r.exercise_key === exerciseKey).map(subjectId)
  ).size;
}

/**
 * Reduce a sequence of gate taps (point values, e.g. 3 / 1 / 0) into the live
 * scorer state for Template C. `exercise` supplies ballCap (static) or
 * pushCount + shotCount (dynamic distribution). Pure - the screen just renders
 * what this returns.
 *
 * Returns { total, balls, phase, phaseBall, phaseTotalBalls, done,
 *           valuePush, valueShot }.
 *   phase: 'push' | 'shot' | 'single'  (single = non-dynamic gate test)
 *   done:  ball cap reached (the screen also auto-stops on time)
 */
export function gateScore(taps, exercise) {
  const t = taps || [];
  const total = t.reduce((s, p) => s + Number(p || 0), 0);
  const balls = t.length;

  if (exercise && exercise.template === 'gates_dynamic') {
    const push = exercise.pushCount || 0;
    const shot = exercise.shotCount || 0;
    const inPush = balls < push;
    const valuePush = t.slice(0, push).reduce((s, p) => s + Number(p || 0), 0);
    const valueShot = t.slice(push, push + shot).reduce((s, p) => s + Number(p || 0), 0);
    return {
      total, balls,
      phase: inPush ? 'push' : 'shot',
      phaseBall: inPush ? balls + 1 : (balls - push) + 1,
      phaseTotalBalls: inPush ? push : shot,
      done: balls >= push + shot,
      valuePush, valueShot,
    };
  }

  const cap = exercise?.ballCap || 0;
  return {
    total, balls,
    phase: 'single',
    phaseBall: balls + 1,
    phaseTotalBalls: cap,
    done: cap > 0 && balls >= cap,
    valuePush: null, valueShot: null,
  };
}
